import { useEffect, useState } from "react";
import { Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtDate, fmtErr } from "../api";

const StatusBadge = ({ value }) => {
  const cls = value === "Approved"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : value === "Rejected"
    ? "bg-red-50 text-[#C5221F] border-red-200"
    : value === "Cancelled"
    ? "bg-slate-100 text-slate-500 border-slate-200"
    : "bg-amber-50 text-amber-700 border-amber-200";
  return <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>{value}</span>;
};

const LeavesAdmin = () => {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("Pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acting, setActing] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await staffApi.get("/leaves");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async (l, status) => {
    setActing(l.id + status);
    try {
      await staffApi.patch(`/leaves/${l.id}`, { status });
      toast.success(`Leave ${status.toLowerCase()} for ${l.user_name}.`);
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setActing(null);
    }
  };

  const filtered = rows.filter((l) => (tab === "All" ? true : l.status === tab));

  return (
    <div data-testid="admin-leaves">
      <h1 className="font-display text-2xl font-bold text-slate-900">Leave Management</h1>
      <p className="mt-1 text-sm text-slate-500">Review and decide employee leave requests.</p>
      <div className="mt-6 flex gap-2" data-testid="leave-admin-tabs">
        {["Pending", "Approved", "Rejected", "All"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`leave-tab-${t.toLowerCase()}`}
            className={`px-4 py-2 text-[11px] font-display font-semibold uppercase tracking-widest border transition-colors ${tab === t ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
            {t}{t === "Pending" ? ` (${rows.filter((l) => l.status === "Pending").length})` : ""}
          </button>
        ))}
      </div>
      {loading ? (
        <p className="mt-8 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="leave-admin-error">{error}</p>
      ) : filtered.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="leave-admin-empty">No {tab.toLowerCase()} leave requests.</p>
      ) : (
        <ul className="mt-6 space-y-3" data-testid="leave-admin-list">
          {filtered.map((l) => (
            <li key={l.id} className="bg-white border border-slate-200 p-5" data-testid={`leave-admin-row-${l.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-display text-sm font-bold text-slate-900">{l.user_name} <span className="font-normal text-slate-400">· {l.leave_type} Leave</span></p>
                  <p className="mt-1 text-xs text-slate-500">{fmtDate(l.start_date)} → {fmtDate(l.end_date)} · requested {fmtDate(l.created_at)}</p>
                  <p className="mt-2 text-sm text-slate-600">{l.reason}</p>
                  {l.status !== "Pending" && l.reviewed_at && (
                    <p className="mt-2 text-[11px] text-slate-400">Reviewed {fmtDate(l.reviewed_at)}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge value={l.status} />
                  {l.status === "Pending" && (
                    <>
                      <button onClick={() => decide(l, "Approved")} disabled={acting} data-testid={`leave-approve-${l.id}`}
                        className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-3.5 py-2 text-[11px] font-display font-semibold uppercase tracking-widest rounded-sm transition-colors">
                        {acting === l.id + "Approved" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Approve
                      </button>
                      <button onClick={() => decide(l, "Rejected")} disabled={acting} data-testid={`leave-reject-${l.id}`}
                        className="inline-flex items-center gap-1 bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white px-3.5 py-2 text-[11px] font-display font-semibold uppercase tracking-widest rounded-sm transition-colors">
                        {acting === l.id + "Rejected" ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LeavesAdmin;
